import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  columnsForView, describeRequired, unansweredFilters,
  type ReportColumnDef, type ReportDef, type ReportDrillColumn, type SessionView,
} from '@project/contracts'
import { Button, Empty, Icon, Modal, Money, Notice, Panel, Table, type ColumnDef } from '../design/index.js'
import { useApi, type DrillResult, type DrillRow } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'
import { formatDate, formatQuantity } from './format.js'
import { ColumnsButton, ExportButton, useChosenColumns } from './columns.js'
import { SavedViews } from './SavedViews.js'
import { StarToggle } from './favourites.js'
import { ReportGrid, reportValue, totalsOver } from './ReportGrid.js'
import { AppliedFilters, FilterControl, patchFilters } from './report-filters.js'
import { ContextStrip, ReportHelp, ReportWarnings, SummaryCards } from './ReportSummary.js'
import {
  currentProject, forgetApplied, openingState, recallApplied, rememberApplied, type MemoryScope,
} from './report-memory.js'

const useReportDefs = () => {
  const api = useApi()
  return useQuery({ queryKey: ['reportDefs'], queryFn: () => api.reportMenu(), staleTime: 10 * 60_000 })
}

/** The Reports menu, in the six groups docs/10 uses. */
export const ReportsHub = ({ companyId }: { companyId: string }) => {
  const defs = useReportDefs()
  const groups = useMemo(() => {
    const byGroup = new Map<string, ReportDef[]>()
    for (const d of defs.data ?? []) {
      const list = byGroup.get(d.group)
      if (list) list.push(d)
      else byGroup.set(d.group, [d])
    }
    return [...byGroup.entries()]
  }, [defs.data])

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>

  return (
    <div className="page">
      <h1 className="page-title">Reports</h1>
      <p className="page-subtitle">Nothing runs until you set the filters — these read across everything.</p>
      {/* Not a report, but this is where the incumbent files it and where
          people look for it (docs/05 §2). */}
      <Panel title="Files">
        <div className="tile-grid">
          <div className="tile">
            <StarToggle menuKey="page:drive" label="Project Drive" />
            <div className="tile__body">
              <Link to={paths.drive(companyId)} className="tile__title">Project Drive</Link>
              <p className="tile__desc">
                Every file on a job — drawings, certificates, the bills that came with a delivery.
              </p>
            </div>
          </div>
        </div>
      </Panel>
      {groups.map(([group, items]) => (
        <Panel key={group} title={group} aside={`${items.length} report${items.length === 1 ? '' : 's'}`}>
          <div className="tile-grid">
            {items.map((d) => (
              <div className="tile" key={d.key}>
                <StarToggle menuKey={`report:${d.key}`} label={d.label} />
                <div className="tile__body">
                  <Link to={paths.report(companyId, d.route)} className="tile__title">{d.label}</Link>
                  <p className="tile__desc">{d.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}


/**
 * The `DETAILS` modal (docs/10).
 *
 * A total on a report is only worth anything if it can be opened. This lists
 * the documents behind one row and links each of them, so an argument about a
 * number ends at a document rather than at a spreadsheet.
 */
const DetailsModal = ({
  companyId, reportKey, label, value, params, declared, onClose,
}: {
  companyId: string
  reportKey: string
  label: string
  value: string
  params: Readonly<Record<string, string>>
  /** The report's own modal columns, when it declares them (REP2-006, REP2-007). */
  declared?: readonly ReportDrillColumn[] | undefined
  onClose: () => void
}) => {
  const api = useApi()
  const details = useQuery({
    queryKey: ['report-details', api.companyId, reportKey, value, params],
    queryFn: () => api.reportDetails(reportKey, value, params),
  })
  const [tab, setTab] = useState(0)

  const allColumns: ColumnDef<DrillRow>[] = [
    {
      key: 'documentNo',
      header: 'Document',
      width: 180,
      render: (row) => (
        <Link to={paths.document(companyId, row.route, row.id)} className="mono">
          {row.documentNo ?? '(not numbered)'}
        </Link>
      ),
    },
    { key: 'docTypeLabel', header: 'Type', width: 180 },
    { key: 'documentDate', header: 'Date', width: 130, render: (r) => formatDate(r.documentDate) },
    { key: 'documentStatus', header: 'Status', type: 'status', width: 140 },
    { key: 'partyName', header: 'Party', width: 220, render: (r) => r.partyName ?? '—' },
    { key: 'reference', header: 'Supplier ref', width: 170, render: (r) => r.reference ?? '—' },
    { key: 'itemName', header: 'Item', render: (r) => r.itemName ?? '—' },
    { key: 'quantity', header: 'Qty', align: 'right', width: 110, render: (r) => formatQuantity(r.quantity) },
    { key: 'amount', header: 'Amount', type: 'money', align: 'right', width: 150 },
  ]
  // A report that declares its modal's columns gets exactly those, in its
  // order, with no money total (Subcontract order execution: Completed qty
  // beside Invoiced qty).
  const declaredColumns = declared?.map((d): ColumnDef<DrillRow> => {
    switch (d.key) {
      case 'documentNo': return { ...allColumns[0]!, header: d.label }
      case 'documentDate': return { key: d.key, header: d.label, width: 130, render: (r) => formatDate(r.documentDate) }
      case 'documentStatus': return { key: d.key, header: d.label, type: 'status', width: 140 }
      case 'amount': return { key: d.key, header: d.label, type: 'money', align: 'right', width: 150 }
      case 'quantity': case 'completedQty': case 'invoicedQty':
        return { key: d.key, header: d.label, align: 'right', width: 130, render: (r) => formatQuantity(r[d.key] ?? null) }
      default: return { key: d.key, header: d.label, render: (r) => r[d.key] ?? '—' }
    }
  })
  const columns = declaredColumns ?? allColumns.filter((c) => {
    // A column nothing in this list answers is left out: the details of a
    // line all name the same item, and only some documents carry a reference.
    if (c.key !== 'reference' && c.key !== 'partyName' && c.key !== 'itemName') return true
    const rows = details.data?.rows ?? []
    return rows.some((r) => r[c.key as 'reference'] !== null && r[c.key as 'reference'] !== undefined && r[c.key as 'reference'] !== '')
  })

  return (
    <Modal
      title={details.data?.label ?? label}
      wide
      footerLeft={details.data && !details.data.tabs ? `${details.data.rows.length} document line${details.data.rows.length === 1 ? '' : 's'}` : ''}
      actions={<Button kind="primary" onClick={onClose}>Ok</Button>}
      onClose={onClose}
    >
      {details.isLoading ? <Loading /> : null}
      {details.error ? <ErrorBlock error={details.error} /> : null}
      {details.data?.header?.length ? (
        <dl className="rt-drill-head">
          {details.data.header.map((h) => (
            <div key={h.label}>
              <dt className="rt-drill-head__label">{h.label}</dt>
              <dd className="rt-drill-head__value" style={{ margin: 0 }}>{h.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {details.data?.tabs ? (
        <DetailExtras companyId={companyId} reportKey={reportKey} data={details.data} tab={tab} onTab={setTab} />
      ) : details.data && details.data.rows.length === 0 ? (
        <Empty title="No details!" body="No details available for the selected entry." />
      ) : details.data ? (
        <>
          <Table
            columns={columns}
            rows={[...details.data.rows]}
            getKey={(row) => `${row.id}:${row.itemName ?? ''}:${row.amount ?? ''}`}
          />
          {declaredColumns ? null : (
            <div className="report-footer">
              <span><strong>Total </strong><Money value={details.data.total} /></span>
            </div>
          )}
        </>
      ) : null}
    </Modal>
  )
}

/**
 * A report's own Details modal (RPT-009, RPT-013, RPT-058): the facts about
 * the row, blocks of figures, the formula the figure came from, and the
 * documents behind it — in tabs when there are several kinds.
 */
const DetailExtras = ({ companyId, reportKey, data, tab, onTab }: {
  companyId: string; reportKey: string; data: DrillResult; tab: number; onTab: (n: number) => void
}) => {
  const tabs = data.tabs ?? []
  const current = tabs[Math.min(tab, tabs.length - 1)]
  return (
    <div className="rt-details">
      {data.blocks?.length ? (
        <div className="rt-cards">
          {data.blocks.map((b) => (
            <section key={b.title} className="rt-card" aria-label={b.title}>
              <h3 className="rt-card__title">{b.title}</h3>
              {b.facts.map((f) => (
                <div key={f.label} className="rt-card__metric rt-card__line">
                  <span className="rt-card__label">{f.label}</span>
                  <span className="rt-card__value">{reportValue(f.type, f.value) ?? '—'}</span>
                </div>
              ))}
            </section>
          ))}
        </div>
      ) : null}
      {data.formula ? <p className="rt-details__formula num">{data.formula}</p> : null}
      {tabs.length > 1 ? (
        <div className="rt-bar" role="tablist">
          {tabs.map((t, i) => (
            <button key={t.key} type="button" role="tab" aria-selected={t === current}
              className={t === current ? 'seg seg--on' : 'seg'} onClick={() => onTab(i)}>
              {t.label} ({t.rows.length})
            </button>
          ))}
        </div>
      ) : null}
      {current ? (
        current.rows.length === 0
          ? <Empty title="No entries" body={`Nothing under ${current.label.toLowerCase()} for this line.`} />
          : (
            <ReportGrid columns={current.columns} rows={current.rows} totals={current.totals ?? {}}
              companyId={companyId} stateKey={`report-details:${reportKey}:${current.key}`} />
          )
      ) : null}
    </div>
  )
}

type AbcClass = 'A' | 'B' | 'C'

/** A row of the ABC analysis whose suggestion differs from what is assigned. */
interface AbcProposal {
  readonly itemId: string
  readonly itemName: string
  readonly from: string
  readonly to: AbcClass
}

const abcProposals = (rows: ReadonlyArray<Record<string, unknown>>): AbcProposal[] => {
  const out: AbcProposal[] = []
  const seen = new Set<string>()
  for (const r of rows) {
    const itemId = typeof r['itemId'] === 'string' ? r['itemId'] : ''
    const to = r['suggestedClass']
    const verdict = String(r['verdict'] ?? '')
    // "Matches" and "Special — kept as assigned" are left as they are.
    if (!itemId || seen.has(itemId) || (to !== 'A' && to !== 'B' && to !== 'C')) continue
    if (!verdict.startsWith('Assigned') && !verdict.startsWith('Not classified')) continue
    seen.add(itemId)
    out.push({ itemId, itemName: String(r['itemName'] ?? ''), from: String(r['assignedClass'] ?? '—'), to })
  }
  return out
}

/**
 * docs/24 §3.2 — act on the analysis without opening every item.
 *
 * Shown to owners and administrators only: a class moves the order-by date
 * the MRP gives every job, so it is set like a setting is. Each change goes
 * through the item master and is audited. A class that came from the item's
 * category is set on the item itself, which then takes precedence.
 */
const ApplyAbcClasses = ({ rows }: { rows: ReadonlyArray<Record<string, unknown>> }) => {
  const api = useApi()
  const client = useQueryClient()
  const session = useQuery({ queryKey: ['session'], queryFn: () => api.session(), staleTime: 5 * 60_000 })
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const proposals = useMemo(() => abcProposals(rows), [rows])

  const apply = useMutation({
    mutationFn: () => api.applyAbcClasses(proposals.map((p) => ({ itemId: p.itemId, abcClass: p.to }))),
    onSuccess: (result) => {
      setOpen(false)
      setDone(`${result.applied} class${result.applied === 1 ? '' : 'es'} updated.`)
      void client.invalidateQueries({ queryKey: ['report'] })
    },
  })

  if (session.data?.role === 'MEMBER') return null
  if (proposals.length === 0) return done ? <Notice kind="ok">{done} Every material now matches its suggestion.</Notice> : null

  return (
    <>
      <div className="row row--wrap" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <span className="helper">
          {proposals.length} material{proposals.length === 1 ? '' : 's'} assigned a class the analysis would not suggest.
        </span>
        <Button kind="primary" icon={<Icon name="check" size={18} />} onClick={() => { apply.reset(); setOpen(true) }}>
          Apply suggested classes ({proposals.length})
        </Button>
      </div>
      {open ? (
        <Modal
          title="Apply the suggested classes"
          subtitle="Each material below gets the class the analysis suggests. The MRP's order-by dates move with it, for every job."
          wide
          footerLeft={`${proposals.length} material${proposals.length === 1 ? '' : 's'}`}
          actions={(
            <>
              <Button kind="ghost" onClick={() => setOpen(false)} disabled={apply.isPending}>Cancel</Button>
              <Button kind="primary" onClick={() => apply.mutate()} disabled={apply.isPending}>
                {apply.isPending ? 'Applying…' : `Apply ${proposals.length}`}
              </Button>
            </>
          )}
          onClose={() => setOpen(false)}
        >
          {apply.error ? <ErrorBlock error={apply.error} /> : null}
          <Table
            columns={[
              { key: 'itemName', header: 'Material' },
              { key: 'from', header: 'Now', width: 140 },
              { key: 'to', header: 'Becomes', width: 120, render: (p: AbcProposal) => <strong>{p.to}</strong> },
            ]}
            rows={proposals}
            getKey={(p: AbcProposal) => p.itemId}
          />
          <p className="helper" style={{ marginTop: 12, marginBottom: 0 }}>
            S (special or customised) is never changed from here. A class that came from the category is set on the item itself.
          </p>
        </Modal>
      ) : null}
    </>
  )
}

/** The Summary breakup table, in its own columns, without the row that feeds the cards (RPT-011). */
const SummaryBreakup = ({ def, rows, companyId }: { def: ReportDef; rows: ReadonlyArray<Record<string, unknown>>; companyId: string }) => {
  const summary = def.summary!
  const columns = summary.breakupColumns ?? []
  const body = rows.filter((r) => !(summary.rowKey && summary.totalRow !== undefined && String(r[summary.rowKey]) === summary.totalRow))
  return (
    <>
      {summary.breakupTitle ? <h2 className="rt-section-title">{summary.breakupTitle}</h2> : null}
      <ReportGrid columns={columns} rows={body} totals={totalsOver(columns, body)} companyId={companyId}
        stateKey={`report-breakup:${def.key}`} />
    </>
  )
}

/** Any report, drawn entirely from its definition. */
export const ReportPage = ({ companyId, reportRoute, query }: {
  companyId: string; reportRoute: string; query: Query
}) => {
  const api = useApi()
  const client = useQueryClient()
  const defs = useReportDefs()
  const def = defs.data?.find((d) => d.route === reportRoute)

  // Last-used filters and the carried project are this person's (RPT-072).
  const session = client.getQueryData<SessionView>(['session'])
  const scope = useMemo<MemoryScope>(
    () => ({ companyId, userId: session?.userId ?? 'me' }), [companyId, session?.userId])

  const fromQuery = Object.keys(query).length > 0
  const [draft, setDraft] = useState<Record<string, string>>(query as Record<string, string>)
  const [applied, setApplied] = useState<Record<string, string> | null>(
    fromQuery ? (query as Record<string, string>) : null,
  )
  // The filters are a drawer: open while there is nothing to show, closed
  // once a run is on screen (RPT-022).
  const [filtersOpen, setFiltersOpen] = useState(!fromQuery)

  const seeded = useRef(false)
  useEffect(() => {
    // Once the definition arrives. A report opened without a query starts on
    // the filters last applied to it, else its default windows, and runs only
    // when that is what the reference does (RPT-024, RPT-072).
    if (seeded.current || !def || fromQuery) return
    seeded.current = true
    const opening = openingState(def, {}, { last: recallApplied(scope, def.key), project: currentProject(scope) })
    setDraft(opening.draft)
    setApplied(opening.applied)
    setFiltersOpen(opening.applied === null)
  }, [def, fromQuery, scope])

  useEffect(() => {
    if (def && applied) rememberApplied(scope, def.key, applied)
  }, [def, applied, scope])

  // A link may open a particular tab (Purchase savings, Detailed).
  const [tab, setView] = useState<string>(typeof query['view'] === 'string' && query['view'] ? query['view'] : 'summary')
  const [drill, setDrill] = useState<{ value: string; label: string; column?: string } | null>(null)

  // Tabs that are different tables (INT-004, RPT-127), or a filter that picks
  // the table (Material progress: Mode). Otherwise Summary / Detailed.
  const tabs = def?.views ?? null
  const view = def?.viewBy
    ? (applied?.[def.viewBy] ?? def.views?.[0]?.key ?? 'summary')
    : tabs?.length ? (tabs.some((t) => t.key === tab) ? tab : tabs[0]!.key) : tab
  const viewColumns = useMemo(() => (def ? columnsForView(def, view) : []), [def, view])

  const params = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(applied ?? {})) if (v) out[k] = v
    if (def?.hasSummaryDetail || (tabs?.length && !def?.viewBy)) out['view'] = view
    return out
  }, [applied, view, def?.hasSummaryDetail, def?.viewBy, tabs?.length])

  const chooser = useChosenColumns(
    def ? `report:${def.key}${def.views?.length ? `:${view}` : ''}` : '', viewColumns)
  const run = useQuery({
    queryKey: ['report', api.companyId, def?.key, params],
    queryFn: () => api.runReport(def?.key ?? '', params),
    enabled: Boolean(def?.key) && applied !== null,
  })

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No report at '${reportRoute}'`)} /></div>

  const apply = (values: Record<string, string>): void => {
    setDraft(values)
    setApplied(values)
    setFiltersOpen(false)
    navigate(paths.report(companyId, reportRoute, values))
  }
  const missing = unansweredFilters(def.filters, draft)
  const drillKey = def.drill?.key
  const openDrill = (row: Record<string, unknown>, column?: ReportColumnDef): void => {
    if (!drillKey) return
    const value = row[drillKey]
    if (value === null || value === undefined || value === '') return
    setDrill({
      value: String(value),
      label: column?.drill?.label ?? def.drill?.label ?? 'Details',
      ...(column ? { column: column.key } : {}),
    })
  }
  const asCards = Boolean(def.summary && def.hasSummaryDetail && view === 'summary')

  return (
    <div className="page">
      <Link to={paths.hub(companyId, 'reports')} className="crumb"><Icon name="left" size={15} /> Reports</Link>
      <h1 className="page-title">{def.label}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      {def.caveat ? <div style={{ margin: '16px 0' }}><Notice kind="warn">{def.caveat}</Notice></div> : null}

      <div className="rt-bar">
        <Button kind={filtersOpen ? 'ghost' : 'primary'} icon={<Icon name="filter" size={18} />}
          aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>
          {filtersOpen ? 'Hide filters' : 'Show filters'}
        </Button>
        <ColumnsButton all={viewColumns} state={chooser} title="Customise report columns" />
        {/* The same saved-view machinery every list uses: a report anybody
            runs weekly is one nobody should have to re-filter weekly. */}
        <SavedViews
          scope={`report:${def.key}`}
          filters={draft}
          columns={chooser.chosen}
          onApply={(saved) => {
            if (saved.columns.length > 0) chooser.choose(saved.columns)
            apply({ ...saved.filters })
          }}
        />
        <ExportButton
          href={api.exportUrl('reports', def.key, params, chooser.chosen)}
          disabled={applied === null || (run.data?.count ?? 0) === 0}
          count={run.data?.count ?? 0}
          what="rows"
        />
        <ReportHelp def={def} />
        {tabs?.length && !def.viewBy ? (
          <div className="rt-bar__end">
            {tabs.map((v) => (
              <button key={v.key} type="button" className={view === v.key ? 'seg seg--on' : 'seg'}
                aria-pressed={view === v.key} onClick={() => setView(v.key)}>{v.label}</button>
            ))}
          </div>
        ) : def.hasSummaryDetail ? (
          <div className="rt-bar__end">
            {(['summary', 'detailed'] as const).map((v) => (
              <button key={v} type="button" className={view === v ? 'seg seg--on' : 'seg'}
                aria-pressed={view === v} onClick={() => setView(v)}>{v === 'summary' ? 'Summary' : 'Detailed'}</button>
            ))}
          </div>
        ) : null}
      </div>

      {filtersOpen ? (
        <div className="rt-drawer">
          <Panel title="Filters" aside={missing.length ? `${missing.length} required` : undefined}>
            <div className="field-grid">
              {def.filters.map((f) => (
                <FilterControl key={f.key} filter={f} filters={def.filters} value={draft}
                  onChange={(patch) => setDraft((current) => patchFilters(def.filters, current, patch))} />
              ))}
            </div>
            <div className="rt-drawer__actions">
              <Button kind="primary" onClick={() => apply(draft)}>Apply</Button>
              <Button kind="ghost" onClick={() => {
                setDraft({})
                setApplied(null)
                forgetApplied(scope, def.key)
              }}>Clear</Button>
            </div>
          </Panel>
        </div>
      ) : applied ? <AppliedFilters filters={def.filters} applied={applied} /> : null}

      <div style={{ marginTop: 8 }}>
        {applied === null ? (
          // The reference's words, with what is still missing as a second line.
          <Empty
            title="Apply filters!"
            body={missing.length
              ? `Fill the filter values and click ‘Apply’ button. This report needs ${describeRequired(missing)}.`
              : 'Fill the filter values and click ‘Apply’ button.'}
          />
        ) : run.isLoading ? <Loading />
          : run.error ? <ErrorBlock error={run.error} />
          : !run.data || run.data.rows.length === 0 ? (
            <>
              <ContextStrip companyId={companyId} context={run.data?.context} />
              <Empty title="No entries!"
                body="No entries in the report. Check the filter conditions, change it and try again." />
            </>
          ) : (
            <>
              <ContextStrip companyId={companyId} context={run.data.context} />
              <ReportWarnings companyId={companyId} warnings={run.data.warnings} applied={applied} />
              {def.key === 'abc_analysis' ? <ApplyAbcClasses rows={run.data.rows} /> : null}
              {asCards && def.summary ? <SummaryCards layout={def.summary} columns={viewColumns} rows={run.data.rows} /> : null}
              {asCards && def.summary?.breakup && def.summary.breakupColumns ? (
                <SummaryBreakup def={def} rows={run.data.rows} companyId={companyId} />
              ) : !asCards || def.summary?.breakup ? (
                <ReportGrid
                  columns={chooser.visible}
                  rows={run.data.rows}
                  totals={run.data.totals}
                  companyId={companyId}
                  stateKey={`report:${def.key}`}
                  {...(def.rowTone ? { rowTone: def.rowTone } : {})}
                  {...(def.drill ? { onRowClick: (row: Record<string, unknown>) => openDrill(row) } : {})}
                  {...(def.drill ? { onCellDrill: (row: Record<string, unknown>, c: ReportColumnDef) => openDrill(row, c) } : {})}
                />
              ) : null}
            </>
          )}
      </div>

      {drill ? (
        <DetailsModal
          companyId={companyId}
          reportKey={def.key}
          label={drill.label}
          value={drill.value}
          params={drill.column ? { ...params, column: drill.column } : params}
          declared={def.drill?.columns}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </div>
  )
}
